import { Dashboard } from './pages/Dashboard.tsx';
import { ReportPrint, Reports } from './pages/Reports.tsx';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout.tsx';
import { canImport, isAdmin, isStaff } from './lib/api.ts';
import { useAuth } from './lib/auth.tsx';
import { Audit, Departments, DocumentTitles, DuplicatePeople, OrganizationDetail, Physicians, Users } from './pages/Admin.tsx';
import { AssessmentEditor, Worklist } from './pages/Assessment.tsx';
import { ParticipantDetail, ParticipantList } from './pages/Participants.tsx';
import { Report } from './pages/Report.tsx';
import { EventSummary, OrgPacketSummary } from './pages/EventSummary.tsx';
import { ScreeningSummary } from './pages/ScreeningSummary.tsx';
import { SignQueue } from './pages/Review.tsx';
import { Followups } from './pages/Followups.tsx';
import { ReviewQueue } from './pages/ReviewQueue.tsx';
import { EventDetail, EventList } from './pages/Events.tsx';
import { ImportBatch, ImportList, ImportPage } from './pages/Imports.tsx';
import { FieldApp } from './pages/Field.tsx';
import { Help } from './pages/Help.tsx';
import { ArchiveDepartments, ArchiveReview, ArchiveSearch } from './pages/Archive.tsx';
import { FindRecords } from './pages/FindRecords.tsx';
import { ScanInboxes } from './pages/ScanInboxes.tsx';
import { CoverSheetPrint, CoverSheets } from './pages/CoverSheets.tsx';
import { Portal, PortalHome } from './pages/Portal.tsx';
import { ChangePassword, EnrollMfa, Login, VerifyMfa } from './pages/SignIn.tsx';

export function App() {
  const { me, loading, offline } = useAuth();
  const { pathname } = useLocation();
  // Field mode runs without a server session (offline at events); the field PIN protects it instead.
  if (pathname === '/field' || pathname.startsWith('/field/')) return <Routes><Route path="/field/*" element={<FieldApp />} /></Routes>;
  if (loading) return <div className="auth-wrap"><p className="muted">Loading…</p></div>;
  if (!me) return (
    <>
      {offline && <div className="alert info offline-banner">No connection. Field mode works offline: <Link to="/field">open field mode</Link>.</div>}
      <Login />
    </>
  );
  if (me.next === 'change_password') return <ChangePassword />;
  if (me.next === 'enroll_mfa') return <EnrollMfa />;
  if (me.next === 'verify_mfa') return <VerifyMfa />;
  const staff = isStaff(me.role);
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={staff ? <Dashboard /> : <Navigate to="/portal" replace />} />
        {staff && <Route path="participants" element={<ParticipantList />} />}
        {staff && <Route path="participants/:id" element={<ParticipantDetail />} />}
        {staff && <Route path="assessments" element={<Worklist />} />}
        {staff && <Route path="assessments/:id" element={<AssessmentEditor />} />}
        {staff && <Route path="assessments/:id/report" element={<Report />} />}
        {staff && <Route path="queue" element={<SignQueue />} />}
        {staff && <Route path="review" element={<ReviewQueue />} />}
        {staff && <Route path="followups" element={<Followups />} />}
        {staff && <Route path="events" element={<EventList />} />}
        {staff && <Route path="events/:id" element={<EventDetail />} />}
        {staff && <Route path="events/:id/summary" element={<EventSummary />} />}
        {staff && <Route path="events/:id/screening-summary" element={<ScreeningSummary />} />}
        {staff && <Route path="organizations" element={<Departments />} />}
        {staff && <Route path="organizations/:id" element={<OrganizationDetail />} />}
        {staff && <Route path="organizations/:id/packet-summary" element={<OrgPacketSummary />} />}
        {staff && <Route path="organizations/:id/screening-summary" element={<ScreeningSummary />} />}
        <Route path="departments" element={<Navigate to="/organizations" replace />} />
        <Route path="portal" element={staff ? <Navigate to="/organizations" replace /> : <PortalHome />} />
        <Route path="portal/:id" element={<Portal />} />
        <Route path="help" element={<Help />} />
        <Route path="help/:id" element={<Help />} />
        {canImport(me.role) && <Route path="imports" element={<ImportList />} />}
        {canImport(me.role) && <Route path="imports/:id" element={<ImportBatch />} />}
        {canImport(me.role) && <Route path="imports/pages/:pageId" element={<ImportPage />} />}
        {staff && <Route path="cover-sheets" element={<CoverSheets />} />}
        {staff && <Route path="cover-sheets/print" element={<CoverSheetPrint />} />}
        {staff && <Route path="archive" element={<ArchiveSearch />} />}
        {staff && <Route path="find" element={<FindRecords />} />}
        {canImport(me.role) && <Route path="archive/review" element={<ArchiveReview />} />}
        {isAdmin(me.role) && <Route path="archive/departments" element={<ArchiveDepartments />} />}
        {staff && <Route path="reports" element={<Reports />} />}
        {staff && <Route path="reports/print/:runId" element={<ReportPrint />} />}
        {staff && <Route path="reports/:kind" element={<Reports />} />}
        {staff && <Route path="reports/:kind/:id" element={<Reports />} />}
        {isAdmin(me.role) && <Route path="admin/users" element={<Users />} />}
        {isAdmin(me.role) && <Route path="admin/audit" element={<Audit />} />}
        {isAdmin(me.role) && <Route path="admin/document-titles" element={<DocumentTitles />} />}
        {isAdmin(me.role) && <Route path="admin/physicians" element={<Physicians />} />}
        {isAdmin(me.role) && <Route path="admin/duplicates" element={<DuplicatePeople />} />}
        {isAdmin(me.role) && <Route path="admin/scan-inboxes" element={<ScanInboxes />} />}
        <Route path="*" element={<p className="muted">Page not found.</p>} />
      </Route>
    </Routes>
  );
}
